import { DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, TemplateRef, ViewChild, computed, inject, signal, effect } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { AuthService } from 'src/app/auth.service';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { BillingUsageService } from './billing-usage.service';

echarts.use([PieChart, TooltipComponent, GridComponent, CanvasRenderer]);

interface BreakdownItem {
  label: string;
  key: string;
  credits: number;
  color: string;
  icon: string;
}

const BREAKDOWN: BreakdownItem[] = [
  { key: 'gemini-pro', label: 'MySpace.APIUsageHeaders.TypeValues.gemini-pro', credits: 0, color: '#6366f1', icon: 'auto_awesome' },
  { key: 'gemini-flash', label: 'MySpace.APIUsageHeaders.TypeValues.gemini-flash', credits: 0, color: '#818cf8', icon: 'auto_awesome' },
  { key: 'tts', label: 'MySpace.APIUsageHeaders.TypeValues.tts', credits: 0, color: '#f97316', icon: 'record_voice_over' },
  { key: 'tts-studio', label: 'MySpace.APIUsageHeaders.TypeValues.text-to-speech', credits: 0, color: '#fb923c', icon: 'record_voice_over' },
  { key: 'gemini-tts', label: 'Billing.Type.GeminiTTS', credits: 0, color: '#ea580c', icon: 'record_voice_over' },
  { key: 'stt', label: 'MySpace.APIUsageHeaders.TypeValues.stt', credits: 0, color: '#0ea5e9', icon: 'mic_off' },
  { key: 'translation', label: 'MySpace.APIUsageHeaders.TypeValues.translation', credits: 0, color: '#a855f7', icon: 'translate' },
  { key: 'image-generation', label: 'MySpace.APIUsageHeaders.TypeValues.image-generation', credits: 0, color: '#eab308', icon: 'image' },
  { key: 'vision', label: 'MySpace.APIUsageHeaders.TypeValues.vision', credits: 0, color: '#10b981', icon: 'visibility' },
  { key: 'embedding', label: 'MySpace.APIUsageHeaders.TypeValues.embedding', credits: 0, color: '#ec4899', icon: 'category' },
  { key: 'chime-meeting-per-attendee', label: 'MySpace.APIUsageHeaders.TypeValues.chime-meeting-per-attendee', credits: 0, color: '#0d9488', icon: 'video_call' },
  { key: 'chime-video-composition', label: 'Billing.Type.ChimeVideoComposition', credits: 0, color: '#0d9488', icon: 'video_call' },
  { key: 'chime-video-transcription', label: 'Billing.Type.ChimeVideoTranscription', credits: 0, color: '#0ea5e9', icon: 'closed_caption' },
  { label: 'Global.Other', key: 'other', credits: 0, color: '#94a3b8', icon: 'bolt' },
];

interface BillingItem {
  _id: string;
  cost: number;
  date: string;
  tokens: number;
  type: string;
  borderId?: string; // Wait, actually let's keep the interface clean
  unit: string;
  user?: { _id: string; fullName: string; uid: string };
  documentId?: string;
}

interface BillingFilters {
  startDate?: string;
  endDate?: string;
  type?: string;
  userId?: string;
}

@Component({
  selector: 'app-billing-usage',
  templateUrl: './billing-usage.component.html',
  styleUrl: './billing-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinner,
    MatSlideToggle,
    MatCard,
    MatDialogModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDatepickerModule,
    MatTooltipModule,
    DecimalPipe,
    DatePipe,
    UpperCasePipe,
    TranslatePipe,
    SelectComponent,
    NgxEchartsDirective,
  ],
  providers: [provideNativeDateAdapter(), provideEchartsCore({ echarts })],
})
export class BillingUsageComponent implements OnInit, OnDestroy {
  public sidenavService = inject(SidenavService);
  public isSmallSceen = this.sidenavService.isSmallOrTablet;

  private toggleEffect = effect(() => {
    const isSmall = this.isSmallSceen();
    this.sidenavService.showGlobalToggle.set(!isSmall);
  });

  private billingService = inject(BillingUsageService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private snackbar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  private confirmService = inject(AppConfirmService);
  private breakpointService = inject(BreakpointObserverService);

  isSmallScreen = this.breakpointService.isSmallScreen;
  showTiles = computed(() => this.breakpointService.isSmallScreen() || this.breakpointService.isTabletScreen());

  @ViewChild('explanationDialog') explanationDialog!: TemplateRef<any>;
  @ViewChild(MatSort) sort: MatSort;

  // ── Subscription State ──────────────────────────────────────────────────
  subscriptionStatus = 'none';
  hasPaymentMethod = false;
  overageEnabled = false;
  isTogglingOverage = false;

  // ── Infinite scroll state ───────────────────────────────────────────────
  pageSize = 25;
  pageIndex = 0;
  sortField = '';
  sortDirection = '';
  hasMoreData = true;
  loadingMore = false;

  // ── Table ───────────────────────────────────────────────────────────────
  // No project column for Skillztank (single-tenant workspace)
  displayedColumns: string[] = ['type', 'tokens', 'unit', 'date', 'cost', 'user'];
  dataSource = new MatTableDataSource<BillingItem>();
  totalLength = 0;
  costInfoColumns: string[] = ['name', 'description'];
  costInfoDataSource = new MatTableDataSource([]);

  isLoadingCredits = true;
  availableCredits = 0;
  creditTotal = 0;
  creditUsage = 0;
  trialEnd: number | null = null;
  planPrice: number | null = null;
  planCurrency: string | null = null;
  planInterval: string | null = null;
  animatedRemaining = 0;
  animatedUsed = 0;

  periodStart: Date | null = null;
  periodEnd: Date | null = null;

  /** Feature flag: set to true to show the donut breakdown instead of overage */
  showUsageBreakdown = false;

  get creditUsagePercent(): number {
    return this.creditTotal > 0 ? Math.min(100, Math.round((this.creditUsage / this.creditTotal) * 100)) : 0;
  }

  get creditOverage(): number {
    const diff = this.creditUsage - this.creditTotal;
    return diff > 0 ? diff : 0;
  }

  get hasOverage(): boolean {
    return this.creditOverage > 0;
  }

  get creditsExhausted(): boolean {
    const remaining = this.creditTotal - this.creditUsage;
    return remaining <= 50 && this.subscriptionStatus === 'trialing' && this.hasPaymentMethod;
  }

  get trialDaysRemaining(): number | null {
    if (!this.trialEnd) return null;
    const now = new Date().getTime() / 1000;
    const remaining = this.trialEnd - now;
    return remaining > 0 ? Math.ceil(remaining / (3600 * 24)) : 0;
  }

  get planPriceLabel(): string {
    return this.formatPlanPrice();
  }

  formatPlanPrice(): string {
    if (this.planPrice == null) return '';
    const currency = this.planCurrency ?? 'CAD';
    const formatted = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.planPrice);
    const intervalKey = this.planInterval === 'year' ? 'Billing.Plan.PerYear' : 'Billing.Plan.PerMonth';
    return `${formatted}/${this.translate.instant(intervalKey)}`;
  }

  // ── Charts & Metrics ──────────────────────────────────────────────────
  featureBreakdown: BreakdownItem[] = BREAKDOWN;
  legendExpanded = false;
  private readonly LEGEND_COLLAPSED_COUNT = 2;

  get visibleBreakdown(): BreakdownItem[] {
    if (this.legendExpanded) return this.featureBreakdown;
    return this.featureBreakdown.slice(0, this.LEGEND_COLLAPSED_COUNT);
  }

  get hasHiddenLegendItems(): boolean {
    return this.featureBreakdown.length > this.LEGEND_COLLAPSED_COUNT;
  }

  get donutOptions(): any {
    const breakdownData = this.featureBreakdown;
    if (!breakdownData || breakdownData.length === 0) return {};

    const seriesData = breakdownData.map((item) => ({
      value: Number(item.credits.toFixed(2)),
      name: this.translate.instant(item.label),
      itemStyle: { color: item.color },
    }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const val = typeof params.value === 'number' ? params.value.toFixed(2).replace(/\.00$/, '') : params.value;
          const pct = typeof params.percent === 'number' ? params.percent.toFixed(2).replace(/\.00$/, '') : params.percent;
          return `${params.name}: ${val} ${this.translate.instant('Billing.Credits')} (${pct}%)`;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#e2e8f0',
        textStyle: {
          color: '#334155',
          fontSize: 12,
        },
        padding: [8, 12],
      },
      series: [
        {
          type: 'pie',
          radius: ['75%', '90%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 0,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          data: seriesData,
        },
      ],
      animation: true,
    };
  }

  get displayAvailableCredits(): number {
    return Math.max(0, this.creditTotal - this.creditUsage);
  }

  // ── Filters ─────────────────────────────────────────────────────────────
  filters: BillingFilters = {};
  dateRangeGroup = this.fb.group({ start: [null as Date | null], end: [null as Date | null] });
  selectedType: string | null = null;
  selectedUserId: string | null = null;
  private filterDebounce: ReturnType<typeof setTimeout> | null = null;

  typeOptions: SelectOption[] = [
    { value: 'gemini-flash', label: 'Billing.Type.GeminiFlash' },
    { value: 'gemini-pro', label: 'Billing.Type.GeminiPro' },
    { value: 'tts', label: 'Billing.Type.TTS' },
    { value: 'tts-studio', label: 'Billing.Type.TTSStudio' },
    { value: 'gemini-tts', label: 'Billing.Type.GeminiTTS' },
    { value: 'stt', label: 'Billing.Type.STT' },
    { value: 'translation', label: 'Billing.Type.Translation' },
    { value: 'vision', label: 'Billing.Type.Vision' },
    { value: 'image-generation', label: 'Billing.Type.ImageGeneration' },
    { value: 'embedding', label: 'Billing.Type.Embedding' },
    { value: 'chime-meeting-per-attendee', label: 'Billing.Type.ChimeMeetingPerAttendee' },
    { value: 'chime-video-composition', label: 'Billing.Type.ChimeVideoComposition' },
    { value: 'chime-video-transcription', label: 'Billing.Type.ChimeVideoTranscription' },
  ];
  userOptions: SelectOption[] = [];

  ngOnInit() {
    this.setupDateRangeListener();
    this.getAvailableCredits();
    this.getBillingStats();
    this.fetchData();
  }

  setupDateRangeListener() {
    this.route.queryParams.subscribe((params) => {
      if (params['checkout'] === 'success') {
        this.translate.get('Billing.CheckoutSuccess').subscribe(res => this.snackbar.open(res, 'Ok', { duration: 5000 }));
      } else if (params['checkout'] === 'canceled') {
        this.translate.get('Billing.CheckoutCanceled').subscribe(res => this.snackbar.open(res, 'Ok', { duration: 5000 }));
      } else if (params['error'] === 'subscription_inactive') {
        this.translate.get('Billing.SubscriptionInactiveError').subscribe(res => this.snackbar.open(res, 'Ok', { duration: 8000 }));
      }
    });

    const keys = this.typeOptions
      .map((t) => t.label)
      .concat([
        'Billing.Explanation.GeminiFlash',
        'Billing.Explanation.GeminiPro',
        'Billing.Explanation.GeminiTTS',
        'Billing.Explanation.STT',
        'Billing.Explanation.Translation',
        'Billing.Explanation.Vision',
        'Billing.Explanation.ImageGeneration',
        'Billing.Explanation.Embedding',
        'Billing.Explanation.ChimeVideoComposition',
        'Billing.Explanation.ChimeVideoTranscription',
      ]);

    const loadTranslations = () => {
      this.translate.get(keys).subscribe((t) => {
        this.costInfoDataSource.data = [
          { name: t['Billing.Type.GeminiFlash'], description: t['Billing.Explanation.GeminiFlash'] },
          { name: t['Billing.Type.GeminiPro'], description: t['Billing.Explanation.GeminiPro'] },
          { name: t['Billing.Type.GeminiTTS'], description: t['Billing.Explanation.GeminiTTS'] },
          { name: t['Billing.Type.STT'], description: t['Billing.Explanation.STT'] },
          { name: t['Billing.Type.Translation'], description: t['Billing.Explanation.Translation'] },
          { name: t['Billing.Type.Vision'], description: t['Billing.Explanation.Vision'] },
          { name: t['Billing.Type.ImageGeneration'], description: t['Billing.Explanation.ImageGeneration'] },
          { name: t['Billing.Type.Embedding'], description: t['Billing.Explanation.Embedding'] },
          { name: t['Billing.Type.ChimeVideoComposition'], description: t['Billing.Explanation.ChimeVideoComposition'] },
          { name: t['Billing.Type.ChimeVideoTranscription'], description: t['Billing.Explanation.ChimeVideoTranscription'] },
        ];
      });
    };

    loadTranslations();
    this.translate.onLangChange.subscribe(() => loadTranslations());
    this.loadUsers();
  }

  fetchData() {
    this.getBillingUsage(true);
  }

  loadUsers() {
    this.authService.getWorkspaceUsers(100, 0, '', '', '').subscribe({
      next: (res: { list: { _id: string; name: string; email: string; uid: string }[]; total: number }) => {
        this.userOptions = res.list.map((u) => ({ value: u.uid, label: u.name || u.email }));
      },
    });
  }

  onFilterChange() {
    if (this.filterDebounce) clearTimeout(this.filterDebounce);
    this.filterDebounce = setTimeout(() => {
      const { start, end } = this.dateRangeGroup.value;
      this.filters = {
        startDate: start ? (start as Date).toISOString().split('T')[0] : undefined,
        endDate: end ? (end as Date).toISOString().split('T')[0] : undefined,
        type: this.selectedType ?? undefined,
        userId: this.selectedUserId ?? undefined,
      };
      this.getBillingUsage(true);
    }, 300);
  }

  clearFilters() {
    this.dateRangeGroup.reset();
    this.selectedType = null;
    this.selectedUserId = null;
    this.filters = {};
    this.getBillingUsage(true);
  }

  openExplanationDialog() {
    this.dialog.open(this.explanationDialog, {
      width: '600px',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });
  }

  get hasActiveFilters(): boolean {
    const { start, end } = this.dateRangeGroup.value;
    return !!(start || end || this.filters.type || this.filters.userId);
  }

  getBillingUsage(reset = false) {
    if (reset) {
      this.pageIndex = 0;
      this.dataSource.data = [];
      this.hasMoreData = true;
    } else {
      this.loadingMore = true;
    }

    this.billingService.getBillingUsage(this.pageSize, this.pageIndex, this.sortField, this.sortDirection, this.filters).subscribe((res) => {
      if (reset) {
        this.dataSource.data = res.data;
      } else {
        this.dataSource.data = [...this.dataSource.data, ...res.data];
      }
      this.totalLength = res.total;
      this.hasMoreData = this.dataSource.data.length < this.totalLength;
      this.loadingMore = false;
    });
  }

  onTableScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120 && this.hasMoreData && !this.loadingMore) {
      this.pageIndex++;
      this.getBillingUsage();
    }
  }

  onSortChange(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction;
    this.getBillingUsage(true);
  }

  getTypeName(type: string) {
    switch (type) {
      case 'gemini-flash':
        return 'Billing.Type.GeminiFlash';
      case 'gemini-pro':
        return 'Billing.Type.GeminiPro';
      case 'tts':
        return 'Billing.Type.TTS';
      case 'tts-studio':
        return 'Billing.Type.TTSStudio';
      case 'gemini-tts':
        return 'Billing.Type.GeminiTTS';
      case 'stt':
        return 'Billing.Type.STT';
      case 'translation':
        return 'Billing.Type.Translation';
      case 'vision':
        return 'Billing.Type.Vision';
      case 'image-generation':
      case 'image-generation-flash':
      case 'image-generation-pro':
        return 'Billing.Type.ImageGeneration';
      case 'embedding':
        return 'Billing.Type.Embedding';
      case 'chime-meeting-per-attendee':
        return 'Billing.Type.ChimeMeetingPerAttendee';
      case 'chime-video-composition':
        return 'Billing.Type.ChimeVideoComposition';
      case 'chime-video-transcription':
        return 'Billing.Type.ChimeVideoTranscription';
      default:
        return 'Billing.Type.' + type;
    }
  }

  getUnitName(unit: string) {
    switch (unit?.toLowerCase()) {
      case 'token':
      case 'tokens':
        return 'Billing.Unit.Tokens';
      case 'character':
      case 'characters':
        return 'Billing.Unit.Characters';
      case 'second':
      case 'seconds':
      case 'attendee-second':
      case 'attendee-seconds':
        return 'Billing.Unit.Seconds';
      case 'minute':
      case 'minutes':
        return 'Billing.Unit.minute';
      case 'image':
      case 'images':
        return 'Billing.Unit.Images';
      default:
        return 'Billing.Unit.' + unit;
    }
  }

  getAvailableCredits() {
    this.isLoadingCredits = true;
    this.billingService.getAvailableCredits().subscribe((res) => {
      this.billingService.creditAlertTriggered.set(false);
      this.availableCredits = res.globalTotal;
      this.creditTotal = res.creditTotal ?? 0;
      this.creditUsage = res.creditUsage ?? 0;
      this.trialEnd = res.trialEnd ?? null;
      this.planPrice = res.planPrice ?? null;
      this.planCurrency = res.planCurrency ?? null;
      this.planInterval = res.planInterval ?? null;
      this.subscriptionStatus = res.subscriptionData?.status || 'none';
      this.hasPaymentMethod = !!res.subscriptionData?.hasPaymentMethod;
      this.overageEnabled = !!res.subscriptionData?.overageEnabled;
      this.isLoadingCredits = false;

      this.animateCounter(Math.max(0, this.creditTotal - this.creditUsage), (v) => (this.animatedRemaining = v));
      this.animateCounter(this.creditUsage, (v) => (this.animatedUsed = v));
      this.cdr.markForCheck();
    });
  }

  getBillingStats() {
    this.billingService.getBillingStats().subscribe((res) => {
      if (res.periodStart) this.periodStart = new Date(res.periodStart);
      if (res.periodEnd) this.periodEnd = new Date(res.periodEnd);

      if (res.featureBreakdown?.length) {
        const mappedBreakdown = res.featureBreakdown.map((f: any) => {
          const defaultConf = BREAKDOWN.find((b) => b.key === f.type) || { label: f.type, color: '#94a3b8', icon: 'settings', credits: 0, key: f.type };
          return {
            key: f.type,
            label: defaultConf.label || f.type,
            credits: f.credits,
            color: defaultConf.color,
            icon: defaultConf.icon,
          };
        });
        mappedBreakdown.sort((a: any, b: any) => b.credits - a.credits);
        this.featureBreakdown = mappedBreakdown;
      }
      this.cdr.markForCheck();
    });
  }

  private animateCounter(target: number, setter: (v: number) => void, duration = 1200) {
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setter(Math.round(target * eased));
      this.cdr.markForCheck();
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  formatNumber(n: number): string {
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'gemini-flash':
        return 'badge-ai';
      case 'gemini-pro':
        return 'badge-ai-pro';
      case 'tts':
      case 'tts-studio':
      case 'gemini-tts':
        return 'badge-tts';
      case 'stt':
        return 'badge-stt';
      case 'translation':
        return 'badge-translation';
      case 'vision':
        return 'badge-vision';
      case 'image-generation':
      case 'image-generation-flash':
      case 'image-generation-pro':
        return 'badge-image';
      case 'embedding':
        return 'badge-embedding';
      case 'chime-meeting-per-attendee':
      case 'chime-video-composition':
      case 'chime-video-transcription':
        return 'badge-chime';
      default:
        return 'badge-ai';
    }
  }

  isUpgrading = signal(false);

  startTrial() {
    this.confirmService
      .confirm({
        title: this.translate.instant('Billing.UpgradeConfirm.Title'),
        message: this.translate.instant('Billing.UpgradeConfirm.Message'),
        yesText: this.translate.instant('Billing.UpgradeConfirm.Confirm'),
      })
      .subscribe((result) => {
        if (result !== 1) return;
        this.isUpgrading.set(true);
        this.cdr.markForCheck();
        this.billingService.createCheckoutSession().subscribe({
          next: (res) => {
            if (res.url) {
              window.location.href = res.url;
            } else {
              this.isUpgrading.set(false);
              this.cdr.markForCheck();
            }
          },
          error: (err) => {
            this.isUpgrading.set(false);
            this.cdr.markForCheck();
            console.error(err);
            this.snackbar.open(this.translate.instant('Billing.ErrorCheckout'), 'Ok', { duration: 3000 });
          },
        });
      });
  }

  manageSubscription() {
    this.billingService.createPortalSession().subscribe({
      next: (res) => {
        if (res.url) {
          window.location.href = res.url;
        }
      },
      error: (err) => {
        console.error(err);
        this.snackbar.open(this.translate.instant('Billing.ErrorPortal'), 'Ok', { duration: 3000 });
      },
    });
  }

  addPaymentMethod() {
    this.billingService.createPaymentMethodSession().subscribe({
      next: (res) => {
        if (res.url) {
          window.location.href = res.url;
        }
      },
      error: (err) => {
        console.error(err);
        this.snackbar.open(this.translate.instant('Billing.ErrorPortal'), 'Ok', { duration: 3000 });
      },
    });
  }

  isConverting = signal(false);

  convertTrial() {
    const priceLabel = this.formatPlanPrice();
    this.confirmService
      .confirm({
        title: this.translate.instant('Billing.UpgradeConfirm.Title'),
        message: this.translate.instant('Billing.UpgradeConfirm.TrialMessage', { price: priceLabel }),
        yesText: this.translate.instant('Billing.UpgradeConfirm.Confirm'),
      })
      .subscribe((result) => {
        if (result !== 1) return;
        this.isConverting.set(true);
        this.cdr.markForCheck();
        this.billingService.convertTrial().subscribe({
          next: (res) => {
            this.isConverting.set(false);
            this.cdr.markForCheck();
            if (res.converted) {
              this.snackbar.open(this.translate.instant('Billing.TrialConverted'), 'Ok', { duration: 4000 });
              this.getAvailableCredits();
            } else if (res.url) {
              window.location.href = res.url;
            }
          },
          error: (err) => {
            this.isConverting.set(false);
            this.cdr.markForCheck();
            console.error(err);
            this.snackbar.open(this.translate.instant('Billing.ErrorConvertTrial'), 'Ok', { duration: 3000 });
          },
        });
      });
  }

  onToggleOverage(event: any) {
    const newState = event.checked;
    this.isTogglingOverage = true;
    this.cdr.markForCheck();

    this.billingService.toggleOverage(newState).subscribe({
      next: (res) => {
        this.overageEnabled = res.overageEnabled;
        this.isTogglingOverage = false;
        if (newState) {
          this.snackbar.open(this.translate.instant('Billing.OverageEnabledSuccess'), 'OK', { duration: 3000 });
        } else {
          this.snackbar.open(this.translate.instant('Billing.OverageDisabledSuccess'), 'OK', { duration: 3000 });
        }
        this.getAvailableCredits();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.isTogglingOverage = false;
        this.overageEnabled = !newState;
        this.snackbar.open(err?.error?.message || this.translate.instant('Global.Error'), 'OK', { duration: 5000 });
        this.cdr.markForCheck();
      }
    });
  }

  activateOverage() {
    if (this.subscriptionStatus === 'trialing') {
      this.confirmService
        .confirm({
          title: this.translate.instant('Billing.OverageActivatePaidTitle'),
          message: this.translate.instant('Billing.OverageActivatePaidMessage'),
          yesText: this.translate.instant('Billing.OverageActivatePaidConfirmBtn'),
        })
        .subscribe((result) => {
          if (result !== 1) return;
          this.isConverting.set(true);
          this.cdr.markForCheck();
          this.billingService.convertTrial().subscribe({
            next: (res) => {
              this.isConverting.set(false);
              this.cdr.markForCheck();
              if (res.converted) {
                this.snackbar.open(this.translate.instant('Billing.TrialConverted'), 'Ok', { duration: 4000 });
                this.enableOverage();
              } else if (res.url) {
                window.location.href = res.url;
              }
            },
            error: (err) => {
              this.isConverting.set(false);
              this.cdr.markForCheck();
              console.error(err);
              this.snackbar.open(this.translate.instant('Billing.ErrorConvertTrial'), 'Ok', { duration: 3000 });
            },
          });
        });
    } else {
      this.confirmService
        .confirm({
          title: this.translate.instant('Billing.OverageConfirmTitle'),
          message: this.translate.instant('Billing.OverageConfirm'),
          yesText: this.translate.instant('Billing.OverageActivateBtn'),
        })
        .subscribe((result) => {
          if (result === 1) {
            this.enableOverage();
          }
        });
    }
  }

  private enableOverage() {
    this.isTogglingOverage = true;
    this.cdr.markForCheck();

    this.billingService.toggleOverage(true).subscribe({
      next: (res) => {
        this.overageEnabled = res.overageEnabled;
        this.isTogglingOverage = false;
        this.snackbar.open(this.translate.instant('Billing.OverageEnabledSuccess'), 'OK', { duration: 3000 });
        this.getAvailableCredits();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.isTogglingOverage = false;
        this.snackbar.open(err?.error?.message || this.translate.instant('Global.Error'), 'OK', { duration: 5000 });
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.sidenavService.showGlobalToggle.set(true);
  }
}
